<template>
  <div
    class="c-virtual-table-row flex items-center transition-colors duration-200"
    :class="{ 'c-virtual-table-row--selected': selected || draggedOver }"
    :draggable="!!selectedIds.length"
    @click="handleClick"
    @dragend="handleDragEnd"
    @dragenter.prevent
    @dragleave="handleDragLeave"
    @dragover="handleDragOver"
    @dragstart="handleDragStart"
    @drop="handleDrop"
  >
    <div
      v-for="column in columns"
      :key="column.key"
      class="px-2 whitespace-nowrap overflow-hidden overflow-ellipsis"
      :class="column.class"
    >
      <slot :item="item" :name="column.key">{{ item[column.key] }}</slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import useThemeVars from '@lib/composables/useThemeVars'

interface TableRowProps {
  item: { id: number | string; [key: string]: any }
  columns: { key: string; title: string; class?: string }[]
  selectedIds: (number | string)[]
}

const props = defineProps<TableRowProps>()
const emit = defineEmits(['click', 'move', 'dragging'])
const draggedOver = ref(false)
const themeVars = useThemeVars()

const selected = computed(() => props.selectedIds.includes(props.item.id))

const dragData = ref<{ itemIds: TableRowProps['item']['id'][]; ghostElem: HTMLElement | null }>({
  itemIds: [],
  ghostElem: null,
})

function handleClick(event: MouseEvent) {
  emit('click', props.item, event)
}

function handleDragStart(event: DragEvent) {
  if (!selected.value) {
    emit('click', props.item)
  }

  event.dataTransfer!.setData('text/plain', JSON.stringify(props.selectedIds))

  const ghostElem = document.createElement('div')
  ghostElem.style.backgroundColor = themeVars.value.primary
  ghostElem.style.color = themeVars.value.base
  ghostElem.className = 'pa-3 w-12 h-6 flex justify-center items-center text-xs font-bold rounded-2'
  ghostElem.textContent = `${props.selectedIds.length}`

  document.body.appendChild(ghostElem)

  event.dataTransfer!.setDragImage(ghostElem, 0, 0)
  dragData.value = { itemIds: props.selectedIds, ghostElem }
  emit('dragging', true)
  setTimeout(() => {
    document.body.removeChild(ghostElem)
  }, 0)
}

function handleDragEnd() {
  dragData.value = { itemIds: [], ghostElem: null }
  emit('dragging', false)
}

function handleDragOver(event: DragEvent) {
  event.preventDefault()
  if (draggedOver.value) return

  draggedOver.value = true
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault()
  draggedOver.value = false
}

function handleDrop(event: DragEvent) {
  event.preventDefault()
  const itemIds = JSON.parse(event.dataTransfer?.getData('text/plain') || '[]')
  emit('move', { targetItemId: props.item.id, itemIds })
  draggedOver.value = false
}
</script>

<style scoped>
.c-virtual-table-row {
  border-bottom: solid 1px v-bind('themeVars.primary5');
}
.c-virtual-table-row--selected {
  background-color: v-bind('themeVars.primary5');
  border-bottom-color: v-bind('themeVars.base');
}
</style>
